import React, { useState, useMemo } from 'react';
import { 
    Briefcase, Plus, Edit, Trash2, Send, ExternalLink, Copy, Download, 
    Calendar, Sparkles, Clock, Hash, Zap, CheckCircle2, AlertCircle, 
    ArrowRight, Rocket, Layout, Target
} from 'lucide-react';
import { motion, AnimatePresence } from "framer-motion";

// Shared UI
import { 
    AppCard, AppButton, SectionTitle, BlackboxLoaderIcon 
} from '../../components/SharedUI';

// Config
import { PLATFORMS } from '../../config/constants';

// Services
import { callGeminiApi } from '../../services/aiService';

// --- SUB-COMPONENTS ---

// 1. STRATEGY MODAL (The "Go Live" Cockpit)
const GoLiveModal = ({ isOpen, onClose, campaign, setNotification, onUpdateCampaign }) => {
    const [strategy, setStrategy] = useState(null);
    const [loadingStrategy, setLoadingStrategy] = useState(false);
    const [scheduleDate, setScheduleDate] = useState(campaign?.scheduledDate || '');

    if (!isOpen || !campaign) return null;

    const platformDetails = PLATFORMS[campaign.platform] || { name: campaign.platform, color: 'bg-zinc-600' };

    const handleGenerateStrategy = async () => {
        setLoadingStrategy(true);
        try {
            // Gap 1 + Gap 5: Pull full Andromeda context bundled onto the campaign object
            // so the strategy is audience-specific rather than hypothesis-only.
            const andromeda = campaign.andromeda_context || {};
            const primaryAudience   = andromeda.primary_audience_profile || 'Broad audience';
            const psychographics    = andromeda.psychographics?.join(', ') || 'Not specified';
            const allocationRationale = andromeda.asset_allocation_rationale || 'Not specified';
            const alignedHook       = andromeda.aligned_hook || campaign.hypothesis || 'Broad Narrative Appeal';
            const hookTrigger       = andromeda.psychological_trigger || '';
            const hookVisual        = andromeda.visual_direction || '';

            // Gap 3: Surface platform fit scores from video analysis if present
            const platformFit       = campaign.platform_fit_scores
                ? `
TRAILER PLATFORM FIT SCORES: ${JSON.stringify(campaign.platform_fit_scores)}`
                : '';

            const prompt = `
            You are an elite Meta/TikTok media buyer optimizing for the Andromeda algorithmic engine.
            "Creative as Targeting" is the operating principle — the creative itself signals to the algorithm which audience to find. Broad targeting always.

            ASSET DETAILS:
            - Asset Title: "${campaign.title}"
            - Format: "${campaign.platform}"
            - Creative Hook: "${alignedHook}"
            ${hookTrigger ? `- Psychological Trigger: "${hookTrigger}"` : ''}
            ${hookVisual  ? `- Visual Direction: "${hookVisual}"` : ''}

            ANDROMEDA AUDIENCE INTELLIGENCE:
            - Primary Audience Profile: "${primaryAudience}"
            - Psychographic Clusters: "${psychographics}"
            - Asset Allocation Rationale: "${allocationRationale}"
            ${platformFit}

            Generate an Andromeda launch strategy for this specific creative asset. Return JSON with exactly 3 keys:

            1. "targeting_directive": A 1-sentence instruction on WHY this specific hook signals to the Andromeda algorithm to self-select the "${primaryAudience}" segment. Reference the hook name and audience directly — do not write a generic "use broad targeting" response.

            2. "budget_velocity": Specific spend-pacing advice for getting THIS asset format (${campaign.platform}) through the learning phase quickly. Include a daily budget minimum and when to scale.

            3. "fatigue_signal": A specific, audience-aware early-warning metric for THIS hook against THIS audience segment. Example format: "For ${primaryAudience}, watch for [specific metric] dropping below [threshold] — this demographic's [specific behavior] is a leading indicator before view-through falls." Do not write a generic thumb-stop percentage.
            `;

            const schema = {
                type: "OBJECT",
                properties: {
                    targeting_directive: { type: "STRING" },
                    budget_velocity: { type: "STRING" },
                    fatigue_signal: { type: "STRING" }
                },
                required: ["targeting_directive", "budget_velocity", "fatigue_signal"]
            };

            const result = await callGeminiApi(prompt, { responseMimeType: "application/json", responseSchema: schema });
            setStrategy(result);
        } catch (error) {
            console.error("Strategy Error:", error);
            setNotification({ type: 'error', message: 'Could not generate strategy. Try again.' });
        } finally {
            setLoadingStrategy(false);
        }
    };

    const handleDownloadCopyDeck = () => {
        const lines = [
            `CAMPAIGN: ${campaign.title}`,
            `FORMAT: ${platformDetails.name}`,
            `LAUNCH TARGET: ${scheduleDate || 'TBD'}`,
            `\n--- FINAL COPY ---\n`,
            campaign.content,
            `\n\n--- ANDROMEDA STRATEGY NOTES ---\n`,
            strategy ? `Targeting: ${strategy.targeting_directive}\nBudget Pacing: ${strategy.budget_velocity}\nFatigue Metric: ${strategy.fatigue_signal}` : "(Generate Strategy to see details)"
        ];

        const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${campaign.title.replace(/\s+/g, '_')}_Andromeda_Deck.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setNotification({ type: 'success', message: 'Launch Deck downloaded.' });
    };

    const handleSaveSchedule = () => {
        onUpdateCampaign(campaign.id, { ...campaign, scheduledDate: scheduleDate, status: 'Scheduled' });
        setNotification({ type: 'success', message: 'Campaign moved to The Incubator.' });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-xl flex items-center justify-center z-50 p-4">
            <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-4xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
            >
                {/* Header */}
                <div className="p-6 border-b border-zinc-800 flex justify-between items-center bg-zinc-900/50">
                    <div>
                        <h3 className="text-xl font-bold text-white flex items-center gap-3 font-poppins">
                            <Rocket className="text-purple-500" size={20} />
                            Launch Protocol
                        </h3>
                        <p className="text-zinc-400 text-xs mt-1 uppercase tracking-widest">Algorithmic Pre-Flight</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full text-zinc-400 hover:text-white transition-colors">
                        <AlertCircle size={20} className="rotate-45" />
                    </button>
                </div>

                <div className="flex-grow overflow-y-auto p-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* LEFT: VISUAL CONFIRMATION */}
                    <div className="lg:col-span-5 space-y-6">
                        <div className="bg-zinc-900/50 p-1 rounded-xl border border-zinc-800">
                            <div className="aspect-[4/5] bg-black rounded-lg overflow-hidden relative">
                                <img src={campaign.creative?.url} alt="Creative" className="w-full h-full object-contain" />
                                <div className={`absolute top-4 right-4 ${platformDetails.color} p-2 rounded-full shadow-lg`}>
                                    {platformDetails.brandIcon && React.createElement(platformDetails.brandIcon, { size: 16, className: "text-white" })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: TACTICAL DATA */}
                    <div className="lg:col-span-7 space-y-6">
                        <div className="space-y-4">
                            <div>
                                <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">Caption Payload</h4>
                                <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800 text-sm text-zinc-300 leading-relaxed font-mono">
                                    {campaign.content}
                                </div>
                            </div>

                            {/* AI Strategy Module */}
                            <div className="bg-gradient-to-r from-purple-900/10 to-blue-900/10 border border-purple-500/20 rounded-xl p-5">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="font-bold text-white flex items-center gap-2 text-sm">
                                        <Sparkles className="text-purple-400" size={14} />
                                        Andromeda Launch Parameters
                                    </h4>
                                    {!strategy && (
                                        <button 
                                            onClick={handleGenerateStrategy} 
                                            disabled={loadingStrategy}
                                            className="text-xs bg-purple-600 hover:bg-purple-500 text-white px-3 py-1.5 rounded-md font-bold transition-colors"
                                        >
                                            {loadingStrategy ? 'Computing...' : 'Calculate Strategy'}
                                        </button>
                                    )}
                                </div>

                                {strategy ? (
                                    <div className="space-y-4 animate-fade-in">
                                        <div className="flex gap-3 items-start">
                                            <Target size={14} className="text-purple-400 mt-1 shrink-0"/>
                                            <div>
                                                <p className="text-[10px] text-purple-300 font-bold uppercase mb-1">Targeting Directive</p>
                                                <p className="text-white text-xs leading-relaxed">{strategy.targeting_directive}</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-3 items-start">
                                            <Zap size={14} className="text-blue-400 mt-1 shrink-0"/>
                                            <div>
                                                <p className="text-[10px] text-blue-300 font-bold uppercase mb-1">Budget Velocity</p>
                                                <p className="text-zinc-300 text-xs leading-relaxed">{strategy.budget_velocity}</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-3 items-start">
                                            <AlertCircle size={14} className="text-red-400 mt-1 shrink-0"/>
                                            <div>
                                                <p className="text-[10px] text-red-300 font-bold uppercase mb-1">Fatigue Signal</p>
                                                <p className="text-zinc-300 text-xs leading-relaxed">{strategy.fatigue_signal}</p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-xs text-zinc-500 italic">Compute the Andromeda strategy before pushing to the Incubator.</p>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="pt-4 border-t border-zinc-800 space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">Test Initiation Date</label>
                                    <div className="flex gap-2">
                                        <input 
                                            type="date" 
                                            value={scheduleDate}
                                            onChange={(e) => setScheduleDate(e.target.value)}
                                            className="flex-1 bg-zinc-950 border border-zinc-700 text-white rounded-lg px-4 py-2.5 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <AppButton onClick={handleDownloadCopyDeck} variant="secondary" className="flex-1 justify-center" icon={Download}>
                                        Assets
                                    </AppButton>
                                    <AppButton onClick={handleSaveSchedule} disabled={!scheduleDate} variant="primary" className="flex-[2] justify-center bg-green-600 hover:bg-green-500" icon={Rocket}>
                                        Push to Incubator
                                    </AppButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

// 2. MISSION CARD (The Campaign Item)
const MissionCard = ({ campaign, onEdit, onDelete, onPromote, onDemote }) => {
    const platform = PLATFORMS[campaign.platform] || PLATFORMS.instagram;
    const PlatformIcon = platform.brandIcon || Layout;

    return (
        <motion.div 
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="group relative bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded-xl overflow-hidden shadow-lg transition-all hover:shadow-xl hover:shadow-black/50"
        >
            {/* Image Preview Area */}
            <div className="aspect-[16/9] bg-black relative overflow-hidden">
                {campaign.creative?.url ? (
                    <img src={campaign.creative.url} alt="Campaign" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-zinc-800">
                        <Layout className="text-zinc-600" size={32} />
                    </div>
                )}
                {/* Platform Badge */}
                <div className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 backdrop-blur-md text-white border border-white/10">
                    <PlatformIcon size={12} />
                </div>
                {/* Date Badge */}
                {campaign.scheduledDate && (
                    <div className="absolute bottom-2 left-2 px-2 py-1 rounded bg-black/60 backdrop-blur-md text-[10px] font-bold text-white border border-white/10 flex items-center gap-1">
                        <Calendar size={10} /> {new Date(campaign.scheduledDate).toLocaleDateString()}
                    </div>
                )}
            </div>

            {/* Content Area */}
            <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                    <h4 className="font-bold text-white text-sm line-clamp-1 pr-4">{campaign.title}</h4>
                    <button onClick={(e) => { e.stopPropagation(); onEdit(campaign); }} className="text-zinc-500 hover:text-white transition-colors">
                        <Edit size={12} />
                    </button>
                </div>

                {/* Algorithmic Matrix Tags (Safely inside the card) */}
                <div className="flex flex-wrap gap-2 mb-3">
                    <span className="bg-purple-500/10 text-purple-400 border border-purple-500/20 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest truncate max-w-full">
                        {campaign.hypothesis || "Creative Testing"}
                    </span>
                    <span className="bg-zinc-800 text-zinc-400 border border-zinc-700 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest">
                        {campaign.platform?.replace('_', ' ') || "Format"}
                    </span>
                </div>

                <p className="text-xs text-zinc-400 line-clamp-2 mb-4 h-8">{campaign.content}</p>

                {/* Footer Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
                    <button onClick={() => onDelete(campaign.id)} className="text-zinc-600 hover:text-red-500 transition-colors">
                        <Trash2 size={14} />
                    </button>
                    
                    {/* Progression Controls */}
                    <div className="flex items-center gap-1">
                        {campaign.status !== 'Draft' && (
                            <button 
                                onClick={() => onDemote(campaign)}
                                className="p-1.5 rounded hover:bg-zinc-800 text-zinc-500 hover:text-white transition-colors"
                                title="Move Back"
                            >
                                <ArrowRight size={14} className="rotate-180" />
                            </button>
                        )}
                        {campaign.status !== 'Live' && (
                            <button 
                                onClick={() => onPromote(campaign)}
                                className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-purple-600 text-xs font-bold text-white transition-all flex items-center gap-1.5"
                            >
                                {campaign.status === 'Draft' ? 'Test' : 'Scale'} <ArrowRight size={12} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

// 3. STATUS COLUMN
const MissionColumn = ({ title, status, campaigns, icon: Icon, color, children }) => {
    return (
        <div className="flex flex-col h-full min-w-[300px] w-full lg:w-1/3">
            {/* Header */}
            <div className={`flex items-center gap-3 p-4 rounded-t-xl border-b border-white/5 ${color} bg-opacity-10 backdrop-blur-sm`}>
                <Icon size={18} className="text-white" />
                <h3 className="font-bold text-sm tracking-widest text-white uppercase">{title}</h3>
                <span className="ml-auto bg-black/40 px-2 py-0.5 rounded text-xs font-mono text-white">{campaigns.length}</span>
            </div>
            
            {/* Lane */}
            <div className="flex-1 bg-black/20 border-x border-b border-white/5 rounded-b-xl p-4 space-y-4 overflow-y-auto custom-scrollbar">
                {children}
                {campaigns.length === 0 && (
                    <div className="h-24 border-2 border-dashed border-zinc-800 rounded-lg flex items-center justify-center text-zinc-600 text-xs uppercase tracking-widest">
                        Empty Sector
                    </div>
                )}
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---

// Gap 4: onRecordWinningHook writes the winning aligned_hook back to filmData.winning_hooks
const CampaignManagerTab = ({ campaigns, updateCampaign, deleteCampaign, setNotification, openCampaignModal, onRecordWinningHook }) => {
    const [goLiveModal, setGoLiveModal] = useState({ isOpen: false, campaign: null });

    // --- PIPELINE LOGIC ---
    const drafts = useMemo(() => campaigns.filter(c => !c.status || c.status === 'Draft'), [campaigns]);
    const scheduled = useMemo(() => campaigns.filter(c => c.status === 'Scheduled'), [campaigns]);
    const live = useMemo(() => campaigns.filter(c => c.status === 'Live'), [campaigns]);

    const handlePromote = (campaign) => {
        if (campaign.status === 'Draft' || !campaign.status) {
            setGoLiveModal({ isOpen: true, campaign: campaign });
        } else if (campaign.status === 'Scheduled') {
            updateCampaign(campaign.id, { ...campaign, status: 'Live' });
            // Gap 4 — Write winning hook back to filmData so Ad Studio can bias future batches
            // toward hooks that have proven to reach Apex Scaling.
            if (campaign.aligned_hook && onRecordWinningHook) {
                onRecordWinningHook(campaign.aligned_hook);
            }
            setNotification({ type: 'success', message: 'Campaign moved to Apex Scaling!' });
        }
    };

    const handleDemote = (campaign) => {
        if (campaign.status === 'Live') {
            updateCampaign(campaign.id, { ...campaign, status: 'Scheduled' });
        } else if (campaign.status === 'Scheduled') {
            updateCampaign(campaign.id, { ...campaign, status: 'Draft', scheduledDate: null });
        }
    };

    return (
        <>
            <GoLiveModal 
                isOpen={goLiveModal.isOpen}
                onClose={() => setGoLiveModal({ isOpen: false, campaign: null })}
                campaign={goLiveModal.campaign}
                setNotification={setNotification}
                onUpdateCampaign={updateCampaign}
            />

            <div className="h-full flex flex-col gap-6">
                {/* TOP BAR */}
                <div className="flex justify-between items-center flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-500/20 rounded-lg border border-purple-500/50">
                            <Briefcase className="text-purple-400" size={24} />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-white font-poppins tracking-tight">Mission Control</h2>
                            <p className="text-xs text-zinc-400">Algorithmic Testing & Scaling</p>
                        </div>
                    </div>
                    <AppButton onClick={() => openCampaignModal(null, true)} icon={Plus} className="bg-white text-black hover:bg-zinc-200">
                        Create Mission
                    </AppButton>
                </div>

                {/* KANBAN BOARD */}
                <div className="flex-1 flex flex-col lg:flex-row gap-6 overflow-x-auto overflow-y-hidden pb-4">
                    
                    {/* COLUMN 1: THE LABORATORY (DRAFTS) */}
                    <MissionColumn 
                        title="The Laboratory" 
                        status="Draft" 
                        campaigns={drafts} 
                        icon={Layout} 
                        color="bg-zinc-800"
                    >
                        <AnimatePresence>
                            {drafts.map(c => (
                                <MissionCard 
                                    key={c.id} 
                                    campaign={c} 
                                    onEdit={(camp) => openCampaignModal(camp, false)} 
                                    onDelete={deleteCampaign} 
                                    onPromote={handlePromote}
                                    onDemote={handleDemote}
                                />
                            ))}
                        </AnimatePresence>
                    </MissionColumn>

                    {/* COLUMN 2: THE INCUBATOR (TESTING) */}
                    <MissionColumn 
                        title="The Incubator" 
                        status="Scheduled" 
                        campaigns={scheduled} 
                        icon={Clock} 
                        color="bg-blue-900"
                    >
                        <AnimatePresence>
                            {scheduled.map(c => (
                                <MissionCard 
                                    key={c.id} 
                                    campaign={c} 
                                    onEdit={(camp) => openCampaignModal(camp, false)} 
                                    onDelete={deleteCampaign} 
                                    onPromote={handlePromote}
                                    onDemote={handleDemote}
                                />
                            ))}
                        </AnimatePresence>
                    </MissionColumn>

                    {/* COLUMN 3: APEX SCALING (WINNING) */}
                    <MissionColumn 
                        title="Apex Scaling" 
                        status="Live" 
                        campaigns={live} 
                        icon={Zap} 
                        color="bg-purple-900"
                    >
                        <AnimatePresence>
                            {live.map(c => (
                                <MissionCard 
                                    key={c.id} 
                                    campaign={c} 
                                    onEdit={(camp) => openCampaignModal(camp, false)} 
                                    onDelete={deleteCampaign} 
                                    onPromote={handlePromote}
                                    onDemote={handleDemote}
                                />
                            ))}
                        </AnimatePresence>
                    </MissionColumn>

                </div>
            </div>
        </>
    );
};

export default CampaignManagerTab;